import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, ExternalLink, Check, X, AlertTriangle } from "lucide-react";
import { adminLaunchList, adminLaunchDecide } from "../api";
import { toast } from "sonner";

/** Admin ops dashboard at /admin/launch — auth required. */
const STATE_TINTS = {
  draft: "bg-slate-100 text-slate-600",
  submitted: "bg-sky-100 text-sky-800",
  analyzing: "bg-sky-100 text-sky-800",
  preview_ready: "bg-emerald-100 text-emerald-800",
  qualified_starter: "bg-emerald-100 text-emerald-800",
  qualified_advanced: "bg-purple-100 text-purple-800",
  qualified_redesign: "bg-blue-100 text-blue-800",
  missing_info: "bg-amber-100 text-amber-800",
  manual_review: "bg-orange-100 text-orange-800",
  not_eligible: "bg-rose-100 text-rose-800",
  spam: "bg-slate-800 text-white",
};

const DECISIONS = [
  { v: "qualified_starter", label: "Starter", icon: Check },
  { v: "qualified_advanced", label: "Advanced", icon: Check },
  { v: "qualified_redesign", label: "Redesign", icon: Check },
  { v: "missing_info", label: "Missing info", icon: AlertTriangle },
  { v: "manual_review", label: "Manual review", icon: AlertTriangle },
  { v: "not_eligible", label: "Not eligible", icon: X },
  { v: "spam", label: "Spam", icon: X },
];

export default function AdminLaunchPage() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [decisionOpen, setDecisionOpen] = useState(null);

  const load = async () => {
    try {
      const d = await adminLaunchList(filter ? { state: filter } : {});
      setRows(d.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign in as admin to see the queue.");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="admin-launch-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-700 font-bold">Ops · Launch Program</div>
            <h1 className="text-3xl font-black text-slate-900">Applicant queue</h1>
            <p className="text-sm text-slate-500 mt-1">Review, override AI decisions, and route applicants to the right plan.</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)}
              className="border border-slate-300 rounded-md h-9 px-2 text-sm bg-white"
              data-testid="admin-launch-filter">
              <option value="">All states</option>
              <option value="submitted">Submitted</option>
              <option value="analyzing">Analyzing</option>
              <option value="preview_ready">Preview ready</option>
              <option value="manual_review">Manual review</option>
              <option value="missing_info">Missing info</option>
              <option value="not_eligible">Not eligible</option>
              <option value="spam">Spam</option>
            </select>
            <Button size="sm" variant="outline" onClick={load}><Search size={14} className="mr-1" /> Reload</Button>
          </div>
        </div>

        <div className="mt-8">
          {!rows && <div className="text-center py-12"><Loader2 className="mx-auto animate-spin text-sky-600" /></div>}
          {rows?.length === 0 && (
            <Card className="p-8 text-center text-sm text-slate-500 border-dashed">No applications match this filter yet.</Card>
          )}
          {rows?.length > 0 && (
            <Card className="p-0 overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">Business</th>
                    <th className="text-left px-4 py-3">Vertical</th>
                    <th className="text-left px-4 py-3">Country</th>
                    <th className="text-left px-4 py-3">State</th>
                    <th className="text-left px-4 py-3">AI decision</th>
                    <th className="text-left px-4 py-3">Score</th>
                    <th className="text-left px-4 py-3">Final</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`admin-launch-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{r.business_name || "(no name)"}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs uppercase text-slate-600">{r.vertical}</td>
                      <td className="px-4 py-3">{r.country}</td>
                      <td className="px-4 py-3">
                        <Badge className={`${STATE_TINTS[r.state] || "bg-slate-100 text-slate-600"} font-semibold text-[10px] uppercase tracking-widest`}>
                          {r.state.replace("_", " ")}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {r.ai_decision ? (
                          <span className="text-xs">{r.ai_decision}</span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{r.readiness_score ?? <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3">
                        {r.final_decision ? (
                          <Badge className="bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-widest">{r.final_decision}</Badge>
                        ) : <span className="text-xs text-slate-400">pending</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <a href={`/launch/preview/${r.public_token}`} target="_blank" rel="noreferrer"
                             className="text-xs text-sky-700 hover:underline flex items-center gap-1"
                             data-testid={`admin-view-${r.id}`}>
                            <ExternalLink size={12} /> preview
                          </a>
                          <Button size="sm" variant="outline"
                            onClick={() => setDecisionOpen({ id: r.id, current: r.final_decision })}
                            data-testid={`admin-decide-${r.id}`}>
                            Decide
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </div>

      {decisionOpen && (
        <DecisionModal appId={decisionOpen.id}
          onClose={() => setDecisionOpen(null)}
          onDone={() => { setDecisionOpen(null); load(); }} />
      )}
    </div>
  );
}

function DecisionModal({ appId, onClose, onDone }) {
  const [decision, setDecision] = useState("");
  const [reasons, setReasons] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!decision) return;
    setBusy(true);
    try {
      await adminLaunchDecide(appId, {
        decision,
        reasons: reasons.split("\n").map(s => s.trim()).filter(Boolean),
        admin_note: note.trim() || null,
      });
      toast.success("Decision saved.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}
         data-testid="admin-decision-modal">
      <Card className="p-6 max-w-lg w-full bg-white" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-900">Set final decision</h3>
        <p className="text-xs text-slate-500 mt-1">This overrides the AI recommendation and is recorded in the audit log.</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {DECISIONS.map((d) => (
            <button key={d.v} type="button" onClick={() => setDecision(d.v)}
              data-testid={`decision-${d.v}`}
              className={`text-left px-3 py-2 rounded-md border text-sm flex items-center gap-2 ${
                decision === d.v
                  ? "bg-sky-50 border-sky-500 text-sky-900 font-semibold"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}>
              <d.icon size={13} /> {d.label}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <label className="text-xs font-semibold text-slate-600">Reasons (one per line)</label>
          <textarea rows={3} value={reasons} onChange={(e) => setReasons(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            placeholder="e.g.\nNeeds hosting quote\nContact info incomplete"
            data-testid="decision-reasons" />
        </div>
        <div className="mt-3">
          <label className="text-xs font-semibold text-slate-600">Internal note (optional)</label>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            data-testid="decision-note" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={!decision || busy}
            className="bg-slate-900 hover:bg-slate-800" data-testid="decision-submit">
            {busy ? <Loader2 className="mr-1 animate-spin" size={14} /> : null}
            Save decision
          </Button>
        </div>
      </Card>
    </div>
  );
}
